import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Basic from "./Basic";
import CurbSide from "./CurbSide";
import StoreHours from "./StoreHours";
import { createSeasonalTiming } from "./api";
import { toast } from "react-toastify";
import useValidations from "../../../../utils/hooks/useValidations";
import Delivery from "./Delivery";
import DineHours from "./DineHours";
import GateDelivery from "./GateDelivery";

const useAddSeasonalStore = ({
  refetch,
  handleCreateClick,
  storeState,
  setStoreState,
  basicData,
}) => {
  const [moveToTab, setMoveToTab] = useState(false);
  const [isStoreCreate, setIsStoreCreate] = useState(false);
  const { englishRegex } = useValidations();
  const storeData = basicData?.data?.store?.filter((item) =>
    storeState?.itemData?.customer_keys?.includes(item?._id),
  );
  const initialValues = {
    season_name: storeState?.isDuplicate
      ? ""
      : storeState?.itemData?.season_name || "",
    start_date: storeState?.isDuplicate
      ? ""
      : storeState?.itemData?.start_date || "",
    end_date: storeState?.isDuplicate
      ? ""
      : storeState?.itemData?.end_date || "",
    store: storeState?.isDuplicate ? [] : storeData || [],
    status: storeState?.isDuplicate ? 2 : storeState?.itemData?.status || 2,
    pick_up: storeState?.itemData?.pick_up || 2,
    dine_in: storeState?.itemData?.dine_in || 2,
    curb_side: storeState?.itemData?.curb_side || 2,
    office_delivery: storeState?.itemData?.office_delivery || 2,
    gate_delivery: storeState?.itemData?.gate_delivery || 2,
    pickup_hours: storeState?.itemData?.pickup_hours || {},
    dine_in_hours: storeState?.itemData?.dine_in_hours || {},
    curbside_hours: storeState?.itemData?.curbside_hours || {},
    office_delivery_hours: storeState?.itemData?.office_delivery_hours || {},
    gate_delivery_hours: storeState?.itemData?.gate_delivery_hours || {},
  };
  const validation = Yup.object({
    season_name: Yup.string()
      .trim()
      .matches(englishRegex, "Enter in english letters")
      .required("Enter Season Name")
      .max(80, "Enter Valid Season Name"),
    start_date: Yup.string().required("Please enter From date"),
    end_date: Yup.string().required("Please enter To date"),
    store: Yup.array()
      .min(1, "At least one store must be selected")
      .required("Select store"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: validation,

    onSubmit: (values) => {
      setIsStoreCreate(true);
      let data = {
        season_name: values?.season_name,
        start_date: values?.start_date,
        end_date: values?.end_date,
        customer_keys: values.store?.map((store) => store._id) ?? [],
        store_names: values.store?.map((store) => store.name) ?? [],

        pick_up: values?.pick_up,
        dine_in: values?.dine_in,
        curb_side: values?.curb_side,
        office_delivery: values?.office_delivery,
        gate_delivery: values?.gate_delivery,

        pickup_hours: values?.pickup_hours,
        dine_in_hours: values?.dine_in_hours,
        curbside_hours: values?.curbside_hours,
        office_delivery_hours: values?.office_delivery_hours,
        gate_delivery_hours: values?.gate_delivery_hours,
        status: values?.status,
      };
      let newObj = Object.entries(data);
      let subData = newObj
        .filter(
          (item) => item[1] !== undefined && item[1] !== "" && item[1] !== null,
        )
        .reduce((a, v) => ({ ...a, [v[0]]: v[1] }), {});
      if (!storeState?.isEdit) {
        createSeasonalTiming(subData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Seasonal Store Timing Created Successfully`);
            handleCreateClick();
            refetch();
            setIsStoreCreate(false);
            setStoreState((prev) => ({
              ...prev,
              clearSelection: true,
            }));
          } else if (
            (response?.data?.error_code === 403 ||
              response?.data?.status_code === 422) &&
            response?.data?.errors
          ) {
            Object.keys(response?.data?.errors).forEach((field) => {
              if (field === "office_delivery_hours") {
                formik.setFieldError(
                  "office_delivery_hours",
                  response?.data?.errors[field],
                );
              } else {
                formik.setFieldError(field, response?.data?.errors[field]);
              }
              handleMoveToErrorTab();
              setIsStoreCreate(false);
            });
          } else {
            toast.error(`Something went wrong !`);
            handleCreateClick();
            setIsStoreCreate(false);
            setStoreState((prev) => ({
              ...prev,
              clearSelection: true,
            }));
          }
        });
      } else {
        let updateStoreData = {
          season_time_id: storeState?.itemData?._id,
          ...subData,
        };
        createSeasonalTiming(updateStoreData).then((response) => {
          if (response?.data?.status_code === 200) {
            toast.success(`Seasonal Store Timing Updated Successfully`);
            handleCreateClick();
            setIsStoreCreate(false);
            refetch();
            setStoreState((prev) => ({
              ...prev,
              clearSelection: true,
            }));
          } else if (
            response?.data?.status_code === 422 &&
            response?.data?.errors
          ) {
            Object.keys(response?.data?.errors).forEach((field) => {
              formik.setFieldError(field, response?.data?.errors[field]);
              handleMoveToErrorTab();
              setIsStoreCreate(false);
              toast.error(response?.data?.errors[field]);
            });
          } else {
            toast.error(`Something went wrong !`);
            handleCreateClick();
            setIsStoreCreate(false);
            setStoreState((prev) => ({
              ...prev,
              clearSelection: true,
            }));
          }
        });
      }
      // }
    },
  });

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  const handleTabClick = (tab) => {
    setStoreState((prev) => ({
      ...prev,
      activeTab: tab?.label,
    }));
  };

  function setActiveTabByName(active) {
    setMoveToTab(false);
    const updatedTab = tabs.find((tab) => tab?.label === active);
    setStoreState((prev) => ({
      ...prev,
      activeTab: updatedTab?.label,
    }));
  }

  const tabsAndFields = [
    {
      label: "Basic",
      fields: ["season_name", "start_date", "end_date", "stores", "status"],
    },
    {
      label: "Store Hours",
      fields: ["pick_up", "pickup_hours"],
    },
    {
      label: "Dine-in Hours",
      fields: ["dine_in", "dine_in_hours"],
    },
    {
      label: "Curbside Hours",
      fields: ["curb_side", "curbside_hours"],
    },
    {
      label: "Office Delivery Hours",
      fields: ["office_delivery", "office_delivery_hours"],
    },
    {
      label: "Gate Delivery Hours",
      fields: ["gate_delivery", "gate_delivery_hours"],
    },
  ];
  useEffect(() => {
    if (moveToTab === true) {
      handleMoveToErrorTab();
    }
    setMoveToTab(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.errors, moveToTab]);

  function calculateCompletionStatus(fields) {
    let finalArray = fields?.filter(
      (value) =>
        value !== "dial_code" &&
        value !== "sec_dial_code" &&
        value !== "status" &&
        value !== "pick_up" &&
        value !== "curb_side" &&
        value !== "dine_in",
    );

    if (finalArray.includes("pickup_hours")) {
      // Check if at least one value exists in pickup_hours
      if (
        formik?.values.pickup_hours &&
        Object.keys(formik?.values.pickup_hours).length > 0
      ) {
        finalArray.push("pickup_hours");
      }
    }
    if (finalArray.includes("dine_in_hours")) {
      // Check if at least one value exists in pickup_hours
      if (
        formik?.values.dine_in_hours &&
        Object.keys(formik?.values.dine_in_hours).length > 0
      ) {
        finalArray.push("dine_in_hours");
      }
    }
    if (finalArray.includes("curbside_hours")) {
      // Check if at least one value exists in curbside_hours
      if (
        formik?.values.curbside_hours &&
        Object.keys(formik?.values.curbside_hours).length > 0
      ) {
        finalArray.push("curbside_hours");
      }
    }

    if (finalArray.includes("office_delivery_hours")) {
      // Check if at least one value exists in delivery hours
      if (
        formik?.values.office_delivery_hours &&
        Object.keys(formik?.values.office_delivery_hours).length > 0
      ) {
        finalArray.push("office_delivery_hours");
      }
    }
    if (finalArray.includes("gate_delivery_hours")) {
      // Check if at least one value exists in gate_delivery_hours
      if (
        formik?.values.gate_delivery_hours &&
        Object.keys(formik?.values.gate_delivery_hours).length > 0
      ) {
        finalArray.push("gate_delivery_hours");
      }
    }
    const completedFields = finalArray?.filter((field) => {
      // Check if the field exists and is not an empty object
      return (
        formik?.values[field] && Object.keys(formik?.values[field]).length > 0
      );
    });

    return (completedFields.length / fields.length) * 125;
  }

  const tabs = [
    {
      label: "Basic",
      completed: calculateCompletionStatus([
        "season_name",
        "start_date",
        "end_date",
        "stores",
        "status",
      ]),
    },
    {
      label: "Store Hours",
      completed: calculateCompletionStatus(["pick_up", "pickup_hours"]),
    },
    {
      label: "Dine-in Hours",
      completed: calculateCompletionStatus(["dine_in", "dine_in_hours"]),
    },
    {
      label: "Curbside Hours",
      completed: calculateCompletionStatus(["curb_side", "curbside_hours"]),
    },
    {
      label: "Office Delivery Hours",
      completed: calculateCompletionStatus([
        "office_delivery",
        "office_delivery_hours",
      ]),
    },
    {
      label: "Gate Delivery Hours",
      completed: calculateCompletionStatus([
        "gate_delivery",
        "gate_delivery_hours",
      ]),
    },
  ];

  const renderTabContent = () => {
    switch (storeState?.activeTab) {
      case "Basic":
        return (
          <Basic
            formik={formik}
            tabs={tabs}
            getFieldError={getFieldError}
            storeState={storeState}
            basicData={basicData}
            setStoreState={setStoreState}
          />
        );
      case "Store Hours":
        return (
          <StoreHours
            formik={formik}
            tabs={tabs}
            getFieldError={getFieldError}
            storeState={storeState}
            setStoreState={setStoreState}
          />
        );
      case "Dine-in Hours":
        return (
          <DineHours
            formik={formik}
            tabs={tabs}
            getFieldError={getFieldError}
            storeState={storeState}
            setStoreState={setStoreState}
          />
        );
      case "Curbside Hours":
        return (
          <CurbSide
            formik={formik}
            getFieldError={getFieldError}
            tabs={tabs}
            isStoreCreate={isStoreCreate}
            storeState={storeState}
            setStoreState={setStoreState}
          />
        );
      case "Office Delivery Hours":
        return (
          <Delivery
            formik={formik}
            getFieldError={getFieldError}
            tabs={tabs}
            isStoreCreate={isStoreCreate}
            storeState={storeState}
            setStoreState={setStoreState}
          />
        );
      case "Gate Delivery Hours":
        return (
          <GateDelivery
            formik={formik}
            getFieldError={getFieldError}
            tabs={tabs}
            isStoreCreate={isStoreCreate}
            storeState={storeState}
            setStoreState={setStoreState}
          />
        );
      default:
        return null;
    }
  };

  const handleMoveToErrorTab = () => {
    const firstErrorTab = Object.keys(formik.errors)[0];
    if (firstErrorTab && firstErrorTab !== storeState?.activeTab) {
      const errorTab = tabsAndFields?.find((tab) =>
        tab.fields.includes(firstErrorTab),
      );
      setActiveTabByName(errorTab?.label || "Office Delivery Hours");
    } else if (storeState?.pickupTimeError) {
      const hasSpecificError = Object.values(storeState?.pickupTimeError).some(
        (error) =>
          error === "To time should be at least 1 hour after from time",
      );
      if (hasSpecificError) {
        setActiveTabByName("Store Hours");
        return;
      }
    } else if (storeState?.curbTimeError) {
      const hasSpecificError = Object.values(storeState?.curbTimeError).some(
        (error) =>
          error === "To time should be at least 1 hour after from time",
      );
      if (hasSpecificError) {
        setActiveTabByName("Curbside Hours");
        return;
      }
    }
  };

  return {
    tabs,
    activeTab: storeState?.activeTab,
    formik,
    renderTabContent,
    handleTabClick,
    handleMoveToErrorTab,
  };
};

export default useAddSeasonalStore;
